/* -----------------Types--------------- */

import type { Choice, NumChoicesRequired } from "@memorang/types";
import { memo, useEffect, useMemo, useState } from "react";
import Box from "../../Box";
import { getNumPercenetageAnswered } from "../helpers";
/* -----------------Components--------------- */
import ChoiceListItem from "./ChoiceListItem";
import { DraggableList } from "./ItemTypes/Sequence";

export interface ChoicesListProps {
	choices: Choice[];
	handleSelectChoice: (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => void;
	selectedChoiceIds?: string[];
	correctAnswerIds?: string[];
	crossedOutChoiceIds?: string[];
	multiSelect?: boolean;
	isAnswered?: boolean;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
	useCardWrapper?: boolean;
	showCrossOut?: boolean;
	itemId: number;
	correctAnswerOrders?: string[];
	numChoicesRequired?: NumChoicesRequired;
	maxChoiceCount?: number;
	currentUserResponse?: unknown;
	setUserResponse?: (itemId: number, data: unknown) => void;
	handleSkipSequence?: () => void;
	isSequence?: boolean;
	shuffle?: boolean;
	fontSize?: number;
}

const shuffledChoicesByItemId = new Map<number, Choice[]>();

const shuffleChoices = (choices: Choice[]) => {
	return [...choices].sort(() => Math.random() - 0.5);
};

const getDisplayChoices = (
	choices: Choice[],
	itemId: number,
	shuffle?: boolean,
	isSequence?: boolean,
) => {
	if (!shuffle || isSequence) {
		return choices;
	}

	const cached = shuffledChoicesByItemId.get(itemId);
	if (cached) {
		return cached;
	}

	const shuffled = shuffleChoices(choices);
	shuffledChoicesByItemId.set(itemId, shuffled);
	return shuffled;
};

const ChoicesList = ({
	choices,
	handleSelectChoice,
	selectedChoiceIds,
	multiSelect,
	correctAnswerIds,
	crossedOutChoiceIds,
	isAnswered,
	isSummativeTest,
	useCardWrapper = true,
	showCrossOut,
	itemId,
	correctAnswerOrders,
	numChoicesRequired,
	maxChoiceCount,
	currentUserResponse,
	setUserResponse,
	handleSkipSequence,
	isSequence,
	shuffle,
	fontSize,
}: ChoicesListProps) => {
	const isSATA = multiSelect && !isSequence;
	const maxSelectable = numChoicesRequired?.max ?? maxChoiceCount ?? undefined;
	const displayChoices = useMemo(
		() => getDisplayChoices(choices, itemId, shuffle, isSequence),
		[choices, itemId, shuffle, isSequence],
	);

	useEffect(() => {
		if (isSequence && !currentUserResponse && setUserResponse) {
			setUserResponse(
				itemId,
				displayChoices.map((choice) => choice.text!),
			);
		}
	}, [
		displayChoices,
		itemId,
		setUserResponse,
		isSequence,
		currentUserResponse,
	]);

	const [localChoices, setLocalChoices] = useState(displayChoices);

	if (isSequence && setUserResponse) {
		const renderItem = (choice: Choice, index: number, drag: () => void) => {
			const isCorrectAnswer = correctAnswerOrders?.[index] === choice.id;
			const status = selectedChoiceIds?.includes(choice.id)
				? "checked"
				: "unchecked";
			const percentageAnswered = getNumPercenetageAnswered(
				displayChoices,
				choice.numFirstSelections ?? 0,
				isAnswered,
			);

			const isSelected = selectedChoiceIds?.includes(choice.id);

			return (
				<ChoiceListItem
					{...choice}
					text={choice.text!}
					index={index + 1}
					useCardWrapper={useCardWrapper}
					answered={isAnswered}
					isSummativeTest={isSummativeTest}
					multiSelect={multiSelect}
					status={status}
					isSelected={isSelected}
					showCrossOut={showCrossOut}
					isCorrectAnswer={isCorrectAnswer}
					drag={drag}
					percentageAnswered={percentageAnswered}
					fontSize={fontSize}
					handleSelectChoice={(_crossOut) => {
						// handleSelectChoice(choice.id, crossOut);
					}}
				/>
			);
		};

		const handleReorder = (newItems: Choice[]) => {
			setLocalChoices(newItems);
			setUserResponse?.(
				itemId,
				newItems.map((item) => item.text!),
			);
			handleSelectChoice(
				" ",
				false,
				newItems.map((item) => item.id),
			);
		};

		return (
			<DraggableList
				items={localChoices}
				onReorder={handleReorder}
				renderItem={renderItem}
				answered={isAnswered}
				handleSkip={handleSkipSequence}
			/>
		);
	}

	return (
		<Box gap={8}>
			{displayChoices?.map((choice, index) => {
				const isCorrectAnswer = correctAnswerIds?.includes(choice.id);

				const status = selectedChoiceIds?.includes(choice.id)
					? "checked"
					: "unchecked";
				const isCrossedOut = crossedOutChoiceIds?.includes(choice.id);
				const percentageAnswered = getNumPercenetageAnswered(
					displayChoices,
					choice.numFirstSelections ?? 0,
					isAnswered,
				);
				const isSelected = selectedChoiceIds?.includes(choice.id);

				const disabled =
					isSATA &&
					maxSelectable != null &&
					maxSelectable > 0 &&
					(selectedChoiceIds?.length ?? 0) >= maxSelectable &&
					!isSelected;
				return (
					<ChoiceListItem
						key={choice.id}
						{...choice}
						text={choice.text!}
						index={index + 1}
						useCardWrapper={useCardWrapper}
						answered={isAnswered}
						isSummativeTest={isSummativeTest}
						disabled={disabled}
						multiSelect={multiSelect}
						status={status}
						isSelected={isSelected}
						showCrossOut={showCrossOut}
						isCorrectAnswer={isCorrectAnswer}
						isCrossedOut={isCrossedOut}
						percentageAnswered={percentageAnswered}
						fontSize={fontSize}
						handleSelectChoice={(crossOut) =>
							handleSelectChoice(choice.id, crossOut)
						}
					/>
				);
			})}
		</Box>
	);
};

export default memo(ChoicesList, (prevProps, nextProps) => {
	return (
		prevProps.selectedChoiceIds === nextProps.selectedChoiceIds &&
		prevProps.itemId === nextProps.itemId
	);
});
